import React, { useState, useEffect } from 'react';
import "@/App.css";
import './i18n';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from './components/ui/sonner';
import { AnimatePresence, motion } from 'framer-motion';
import { ThemeProvider, useTheme } from './context/ThemeContext';

// Components
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import GrainOverlay from './components/GrainOverlay';
import ScrollToTop from './components/ScrollToTop';
import TranslatorBot from './components/TranslatorBot';
import SignLanguageWidget from './components/SignLanguageWidget';
import AccessibilityBots from './components/AccessibilityBots';

// Public Pages
import HomePage from './pages/HomePage';
import AboutPage from './pages/AboutPage';
import SermonsPage from './pages/SermonsPage';
import PodcastPage from './pages/PodcastPage';
import ProgramsPage from './pages/ProgramsPage';
import EventsPage from './pages/EventsPage';
import BlogPage from './pages/BlogPage';
import PrayerPage from './pages/PrayerPage';
import QAPage from './pages/QAPage';
import PartnersPage from './pages/PartnersPage';
import DonatePage from './pages/DonatePage';
import ContactPage from './pages/ContactPage';
import VolunteerPage from './pages/VolunteerPage';
import ImNewPage from './pages/ImNewPage';
import SmallGroupsPage from './pages/SmallGroupsPage';
import LivestreamPage from './pages/LivestreamPage';
import KidsYouthPage from './pages/KidsYouthPage';
import ResourcesPage from './pages/ResourcesPage';
import PantriesPage from './pages/PantriesPage';
import LocalBusinessPage from './pages/LocalBusinessPage';
import BusinessTeachingPage from './pages/BusinessTeachingPage';
import PantryFinderPage from './pages/PantryFinderPage';
import MissionPage from './pages/MissionPage';
import ImpactStoriesPage from './pages/ImpactStoriesPage';
import AnimalRescuePage from './pages/AnimalRescuePage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import TermsOfServicePage from './pages/TermsOfServicePage';
import FruitBearersPage from './pages/FruitBearersPage';
import TransparencyPage from './pages/TransparencyPage';
import PantryRegistrationPage from './pages/PantryRegistrationPage';
import PantryCheckinPage from './pages/PantryCheckinPage';
import OutreachMissionPage from './pages/OutreachMissionPage';

// Admin Pages
import AdminLogin from './pages/admin/AdminLogin';
import AuthCallback from './pages/admin/AuthCallback';
import AdminLayout from './pages/admin/AdminLayout';
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminSettings from './pages/admin/AdminSettings';
import AdminPageVisibility from './pages/admin/AdminPageVisibility';
import AdminSermons from './pages/admin/AdminSermons';
import AdminBlog from './pages/admin/AdminBlog';
import AdminMessages from './pages/admin/AdminMessages';
import AdminDonations from './pages/admin/AdminDonations';
import AdminVolunteers from './pages/admin/AdminVolunteers';
import AdminAnalytics from './pages/admin/AdminAnalytics';
import AdminUsers from './pages/admin/AdminUsers';
import AdminPodcasts from './pages/admin/AdminPodcasts';
import AdminEvents from './pages/admin/AdminEvents';
import AdminPrograms from './pages/admin/AdminPrograms';
import AdminPantries from './pages/admin/AdminPantries';
import AdminSmallGroups from './pages/admin/AdminSmallGroups';
import AdminPrayers from './pages/admin/AdminPrayers';
import AdminQA from './pages/admin/AdminQA';
import AdminPartners from './pages/admin/AdminPartners';
import AdminLivestream from './pages/admin/AdminLivestream';
import AdminTheme from './pages/admin/AdminTheme';
import AdminBusinesses from './pages/admin/AdminBusinesses';
import AdminBusinessTeaching from './pages/admin/AdminBusinessTeaching';
import AdminActivityLog from './pages/admin/AdminActivityLog';
import AdminGrantWriter from './pages/admin/AdminGrantWriter';
import AdminStories from './pages/admin/AdminStories';
import AdminSuggestions from './pages/admin/AdminSuggestions';
import AdminContentHub from './pages/admin/AdminContentHub';
import AdminInquiries from './pages/admin/AdminInquiries';
import AdminMarketing from './pages/admin/AdminMarketing';
import AdminPantrySystem from './pages/admin/AdminPantrySystem';
import AdminMediaLibrary from './pages/admin/AdminMediaLibrary';
import AdminReviews from './pages/admin/AdminReviews';
import OutreachPage from './pages/OutreachPage';

// Page transition wrapper
const PageTransition = ({ children }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.4 }}
    >
      {children}
    </motion.div>
  );
};

// Layout component with conditional navbar
const PublicLayout = ({ children }) => {
  const location = useLocation();

  return (
    <div className="overflow-x-hidden">
      <Navbar showNav={true} />
      <main className="min-h-screen overflow-x-hidden">
        <AnimatePresence mode="wait">
          <PageTransition key={location.pathname}>
            {children}
          </PageTransition>
        </AnimatePresence>
      </main>
      <Footer />
    </div>
  );
};

function AppRoutes() {
  const location = useLocation();

  // Check for OAuth callback session_id in URL fragment
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes location={location} key={location.pathname}>
      {/* Public Routes */}
      <Route path="/" element={<PublicLayout><HomePage /></PublicLayout>} />
      <Route path="/about" element={<PublicLayout><AboutPage /></PublicLayout>} />
      <Route path="/mission" element={<PublicLayout><MissionPage /></PublicLayout>} />
      <Route path="/impact" element={<PublicLayout><ImpactStoriesPage /></PublicLayout>} />
      <Route path="/animal-rescue" element={<PublicLayout><AnimalRescuePage /></PublicLayout>} />
      <Route path="/sermons" element={<PublicLayout><SermonsPage /></PublicLayout>} />
      <Route path="/podcast" element={<PublicLayout><PodcastPage /></PublicLayout>} />
      <Route path="/programs" element={<PublicLayout><ProgramsPage /></PublicLayout>} />
      <Route path="/events" element={<PublicLayout><EventsPage /></PublicLayout>} />
      <Route path="/blog" element={<PublicLayout><BlogPage /></PublicLayout>} />
      <Route path="/prayers" element={<PublicLayout><PrayerPage /></PublicLayout>} />
      <Route path="/prayer" element={<Navigate to="/prayers" replace />} />
      <Route path="/qa" element={<PublicLayout><QAPage /></PublicLayout>} />
      <Route path="/partners" element={<PublicLayout><PartnersPage /></PublicLayout>} />
      <Route path="/donate" element={<PublicLayout><DonatePage /></PublicLayout>} />
      <Route path="/donate/success" element={<PublicLayout><DonatePage /></PublicLayout>} />
      <Route path="/contact" element={<PublicLayout><ContactPage /></PublicLayout>} />
      <Route path="/volunteer" element={<PublicLayout><VolunteerPage /></PublicLayout>} />
      <Route path="/im-new" element={<PublicLayout><ImNewPage /></PublicLayout>} />
      <Route path="/small-groups" element={<PublicLayout><SmallGroupsPage /></PublicLayout>} />
      <Route path="/watch" element={<PublicLayout><LivestreamPage /></PublicLayout>} />
      <Route path="/kids" element={<PublicLayout><KidsYouthPage /></PublicLayout>} />
      <Route path="/resources" element={<PublicLayout><ResourcesPage /></PublicLayout>} />
      <Route path="/pantries" element={<PublicLayout><PantriesPage /></PublicLayout>} />
      <Route path="/local-businesses" element={<PublicLayout><LocalBusinessPage /></PublicLayout>} />
      <Route path="/business-teaching" element={<PublicLayout><BusinessTeachingPage /></PublicLayout>} />
      
      {/* Standalone Tools */}
      <Route path="/pantry-finder" element={<PantryFinderPage />} />
      <Route path="/pantry-registration" element={<PublicLayout><PantryRegistrationPage /></PublicLayout>} />
      <Route path="/pantry/checkin/:registrationId" element={<PantryCheckinPage />} />
      <Route path="/outreach-mission" element={<PublicLayout><OutreachMissionPage /></PublicLayout>} />
      
      {/* Legal & Info Pages */}
      <Route path="/privacy" element={<PublicLayout><PrivacyPolicyPage /></PublicLayout>} />
      <Route path="/terms" element={<PublicLayout><TermsOfServicePage /></PublicLayout>} />
      <Route path="/transparency" element={<PublicLayout><TransparencyPage /></PublicLayout>} />
      <Route path="/fruit-bearers" element={<PublicLayout><FruitBearersPage /></PublicLayout>} />
      <Route path="/outreach" element={<PublicLayout><OutreachPage /></PublicLayout>} />
      
      {/* Admin Routes */}
      <Route path="/admin/login" element={<AdminLogin />} />
      <Route path="/admin/auth-callback" element={<AuthCallback />} />
      <Route path="/admin" element={<AdminLayout />}>
        <Route path="dashboard" element={<AdminDashboard />} />
        <Route path="analytics" element={<AdminAnalytics />} />
        <Route path="sermons" element={<AdminSermons />} />
        <Route path="podcasts" element={<AdminPodcasts />} />
        <Route path="events" element={<AdminEvents />} />
        <Route path="blog" element={<AdminBlog />} />
        <Route path="programs" element={<AdminPrograms />} />
        <Route path="pantries" element={<AdminPantries />} />
        <Route path="small-groups" element={<AdminSmallGroups />} />
        <Route path="prayers" element={<AdminPrayers />} />
        <Route path="qa" element={<AdminQA />} />
        <Route path="partners" element={<AdminPartners />} />
        <Route path="livestream" element={<AdminLivestream />} />
        <Route path="messages" element={<AdminMessages />} />
        <Route path="volunteers" element={<AdminVolunteers />} />
        <Route path="pages" element={<AdminPageVisibility />} />
        <Route path="donations" element={<AdminDonations />} />
        <Route path="users" element={<AdminUsers />} />
        <Route path="settings" element={<AdminSettings />} />
        <Route path="theme" element={<AdminTheme />} />
        <Route path="businesses" element={<AdminBusinesses />} />
        <Route path="business-teaching" element={<AdminBusinessTeaching />} />
        <Route path="activity-log" element={<AdminActivityLog />} />
        <Route path="grants" element={<AdminGrantWriter />} />
        <Route path="stories" element={<AdminStories />} />
        <Route path="suggestions" element={<AdminSuggestions />} />
        <Route path="content-hub" element={<AdminContentHub />} />
        <Route path="inquiries" element={<AdminInquiries />} />
        <Route path="marketing" element={<AdminMarketing />} />
        <Route path="pantry-system" element={<AdminPantrySystem />} />
        <Route path="media-library" element={<AdminMediaLibrary />} />
        <Route path="reviews" element={<AdminReviews />} />
      </Route>
    </Routes>
  );
}

function App() {
  return (
    <ThemeProvider>
      <div className="App bg-cream">
        <GrainOverlay />
        <Toaster position="top-center" richColors />
        <BrowserRouter>
          <ScrollToTop />
          <AppRoutes />
          <AccessibilityBots />
        </BrowserRouter>
      </div>
    </ThemeProvider>
  );
}

export default App;
